import { Component, inject, input, OnInit } from '@angular/core';

import {
  // components
  CbAccordionComponent,
  CbAccordionContentComponent,
  CbAccordionHeaderComponent,
  CbAccordionPanelComponent,
  CbIconModule,
} from '@chubb/ui-components';
import { AccordionModule } from 'primeng/accordion';
import { Coverage } from '../../../interfaces/coverage.model';
import { Customer } from '../../../interfaces/customer.model';
import { ValidationError } from '../../../interfaces/validation-error.model';
import { CoverageCode } from '../../../shared/enums/coverage-code.enum';
import { CoverageType } from '../../../shared/enums/coverage-type.enum';
import { DataService } from '../../../shared/services/data.service';
import { ReferenceDataService } from '../../../shared/services/reference-data.service';
import { AssuranceExtraLifeCoverComponent } from '../coverages/assurance-extra-life-cover/assurance-extra-life-cover.component';
import { DisablementAcceleratedComponent } from '../coverages/disablement-accelerated/disablement-accelerated.component';
import { DisablementStandaloneComponent } from '../coverages/disablement-standalone/disablement-standalone.component';
import { IncomeCoverExtraComponent } from '../coverages/income-cover-extra/income-cover-extra.component';
import { IncomeCoverComponent } from '../coverages/income-cover/income-cover.component';
import { MortgageRepaymentCoverComponent } from '../coverages/mortgage-repayment-cover/mortgage-repayment-cover.component';
import { PremiumCoverComponent } from '../coverages/premium-cover/premium-cover.component';
import { RedundancyCoverComponent } from '../coverages/redundancy-cover/redundancy-cover.component';
import { SpecificInjuryCoverComponent } from '../coverages/specific-injury-cover/specific-injury-cover.component';
import { TraumaCoverAcceleratedModerateComponent } from '../coverages/trauma-cover-accelerated-moderate/trauma-cover-accelerated-moderate.component';
import { TraumaCoverAcceleratedComponent } from '../coverages/trauma-cover-accelerated/trauma-cover-accelerated.component';
import { TraumaCoverChildComponent } from '../coverages/trauma-cover-child/trauma-cover-child.component';
import { TraumaCoverStandaloneModerateComponent } from '../coverages/trauma-cover-standalone-moderate/trauma-cover-standalone-moderate.component';
import { TraumaCoverStandaloneComponent } from '../coverages/trauma-cover-standalone/trauma-cover-standalone.component';

@Component({
  selector: 'app-coverage-details',
  imports: [
    // components
    CbAccordionComponent,
    CbAccordionPanelComponent,
    CbAccordionHeaderComponent,
    CbAccordionContentComponent,
    AccordionModule,
    CbIconModule,
    AssuranceExtraLifeCoverComponent,
    TraumaCoverChildComponent,
    TraumaCoverAcceleratedComponent,
    TraumaCoverAcceleratedModerateComponent,
    TraumaCoverStandaloneComponent,
    TraumaCoverStandaloneModerateComponent,
    MortgageRepaymentCoverComponent,
    DisablementAcceleratedComponent,
    DisablementStandaloneComponent,
    RedundancyCoverComponent,
    IncomeCoverComponent,
    IncomeCoverExtraComponent,
    PremiumCoverComponent,
    SpecificInjuryCoverComponent,
  ],
  templateUrl: './coverage-details.component.html',
  styleUrl: './coverage-details.component.scss',
})
export class CoverageDetailsComponent implements OnInit {
  protected readonly _dataService: DataService = inject(DataService);
  protected readonly _referenceDataService: ReferenceDataService = inject(ReferenceDataService);

  customerIndex = input<number>(0);
  coverages = input.required<Coverage[]>();
  customers = input.required<Customer[]>();
  initialOpenAccordions = input<string[]>([]);
  coverageErrors = input<ValidationError[]>([]);

  openAccordions: string[] = [];
  coverageType = CoverageType;
  coverageCode = CoverageCode;
  isExpanded: boolean = false;

  ngOnInit(): void {
    if (this.initialOpenAccordions().length > 0) {
      this.openAccordions = this.initialOpenAccordions();
    }
  }

  getValByCode(refDataSetName: string, refCode?: string | null): string | undefined {
    if (refCode != null) {
      return this._referenceDataService.getReferenceDataSetValue(refDataSetName, refCode);
    }
    return '';
  }

  /**
   * Returns the coverage error messages for a given customer and coverage index.
   *
   * @param {number} cusIndex - The index of the customer.
   * @param {number} coverIndex - The index of the coverage.
   * @returns {string[]} An array of error message strings related to the specified customer and coverage.
   */
  showCoverageErrors(cusIndex: number, coverIndex: number): string[] {
    const errors: string[] = [];

    // Check if evaluationResponse and errors exist
    if (this.coverageErrors()) {
      this.coverageErrors().forEach((error: ValidationError) => {
        const loweredPaths = error.path.map(p => p.toLowerCase());

        const hasCustomer = loweredPaths.some(p => p.includes(`customers[${cusIndex}]`));
        const hasCoverage = loweredPaths.some(p => p.includes(`coverages[${coverIndex}]`));

        if (hasCustomer && hasCoverage) {
          errors.push(error.message);
        }
      });
    }
    return errors;
  }
}
